// src/pages/components/Checklist.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, Pencil, Trash2, Check, X,
  CheckCircle2, ListPlus, Sparkles,
} from 'lucide-react';
import { tasksApi, TaskChecklistItem } from '../api/tasks';
import todoApi from '../api/todo';
import ConfirmDialog, { type ConfirmState } from './ConfirmDialog';
import { AutoGrowTextarea } from './AutoGrowTextarea';

interface ChecklistProps {
  taskId: string;
  items: TaskChecklistItem[];
  onRefresh: () => void;
  parentTaskTitle?: string;
  lockToggle?: boolean;
  lockMessage?: string;
}

export default function Checklist({ taskId, items, onRefresh, parentTaskTitle, lockToggle = false, lockMessage }: ChecklistProps) {
  const [newItem, setNewItem] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  // Popup used to surface duplicates ("these items are already in your To-Do
  // list") before we create new to-dos.
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // Local optimistic copy of items — keeps the checklist responsive without
  // forcing the parent page to refetch on every toggle/edit/delete.
  const [localItems, setLocalItems] = useState<TaskChecklistItem[]>(items);
  useEffect(() => { setLocalItems(items); }, [items]);

  const sortedItems = useMemo(
    () =>
      [...localItems].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (ta !== tb) return tb - ta;
        return (b.order_index ?? 0) - (a.order_index ?? 0);
      }),
    [localItems]
  );
  const completedCount = localItems.filter((i) => i.is_done).length;
  const allSelected = sortedItems.length > 0 && selected.size === sortedItems.length;
  const hasSelection = selected.size > 0;

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(sortedItems.map((i) => i.id)));
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItem.trim()) return;
    setAdding(true);
    try {
      const r = await tasksApi.addChecklistItem(taskId, { item: newItem });
      const created = r?.data?.item || r?.data;
      const nowIso = new Date().toISOString();
      if (created && created.id) {
        setLocalItems((prev) => {
          const maxOrder = prev.reduce((m, i) => Math.max(m, i.order_index ?? 0), 0);
          const item: TaskChecklistItem = {
            ...(created as TaskChecklistItem),
            created_at: (created as any)?.created_at || nowIso,
            order_index: (created as any)?.order_index ?? maxOrder + 1,
          };
          return [item, ...prev];
        });
      } else {
        // Fallback: best-effort local stub until the next props sync.
        setLocalItems((prev) => {
          const maxOrder = prev.reduce((m, i) => Math.max(m, i.order_index ?? 0), 0);
          const stub = {
            id: `tmp-${Date.now()}`,
            task_id: taskId,
            item: newItem,
            order_index: maxOrder + 1,
            is_done: false,
            created_at: nowIso,
          } as any as TaskChecklistItem;
          return [stub, ...prev];
        });
      }
      setNewItem('');
      // Sync parent so the new item survives tab unmount/remount.
      onRefresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to add item');
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (itemId: string, isDone: boolean) => {
    // Optimistic flip
    setLocalItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, is_done: !isDone } : i)));
    try {
      await tasksApi.updateChecklistItem(itemId, { is_done: !isDone });
      // Refresh so progress %, done_at, and parent state stay in sync.
      onRefresh();
    } catch (err: any) {
      // Rollback
      setLocalItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, is_done: isDone } : i)));
      toast.error(err?.response?.data?.detail || 'Failed to update item');
    }
  };

  const handleDelete = async (itemId: string) => {
    const snapshot = localItems;
    // Optimistic remove
    setLocalItems((prev) => prev.filter((i) => i.id !== itemId));
    setSelected((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
    try {
      await tasksApi.deleteChecklistItem(itemId);
      onRefresh();
    } catch (err: any) {
      setLocalItems(snapshot);
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const startEdit = (item: TaskChecklistItem) => {
    setEditingId(item.id);
    setEditText(item.item);
  };

  const saveEdit = async () => {
    if (!editingId || !editText.trim()) {
      setEditingId(null);
      return;
    }
    const id = editingId;
    const text = editText.trim();
    const snapshot = localItems;
    // Optimistic rename
    setLocalItems((prev) => prev.map((i) => (i.id === id ? { ...i, item: text } : i)));
    setEditingId(null);
    setEditText('');
    try {
      await tasksApi.updateChecklistItem(id, { item: text });
      toast.success('Item updated');
      onRefresh();
    } catch (err: any) {
      setLocalItems(snapshot);
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
  };

  // Mark all selected items as completed
  const markSelectedComplete = async () => {
    if (!hasSelection) return;
    setBusy(true);
    const ids = Array.from(selected);
    const snapshot = localItems;
    setLocalItems((prev) => prev.map((i) => (ids.includes(i.id) ? { ...i, is_done: true } : i)));
    setSelected(new Set());
    try {
      await Promise.all(ids.map((id) => tasksApi.updateChecklistItem(id, { is_done: true })));
      toast.success(`${ids.length} item${ids.length === 1 ? '' : 's'} marked complete`);
      onRefresh();
    } catch (err: any) {
      setLocalItems(snapshot);
      toast.error(err?.response?.data?.detail || 'Failed to mark complete');
    } finally {
      setBusy(false);
    }
  };

  const fetchExistingChecklistTodoIds = async (): Promise<Set<string>> => {
    const ids = new Set<string>();
    try {
      const responses = await Promise.all([
        todoApi.today().catch(() => null),
        todoApi.upcoming().catch(() => null),
        todoApi.missed().catch(() => null),
      ]);
      for (const r of responses) {
        const personal = (r as any)?.data?.personal || [];
        for (const t of personal) {
          if (t?.source_checklist_item_id) ids.add(t.source_checklist_item_id);
        }
      }
    } catch {
    }
    return ids;
  };

  // Actually create the to-dos for the items that aren't already in the list.
  const createTodos = async (picked: TaskChecklistItem[]) => {
    const title = parentTaskTitle || 'Task checklist';
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayIso = `${yyyy}-${mm}-${dd}`;

    await Promise.all(
      picked.map((it) =>
        todoApi.quickAdd({
          title,
          description: it.item,
          priority: 'MEDIUM',
          status: 'PENDING',
          due_date: todayIso,
          source_task_id: taskId,
          source_checklist_item_id: it.id,
        } as any),
      ),
    );
  };

  // Create a personal to-do for each selected checklist item.
  // If some of the selected items have already been turned into to-dos,
  // show a popup listing them and offer to add only the new ones.
  const addSelectedAsTodos = async () => {
    if (!hasSelection) return;
    setBusy(true);
    try {
      const picked = sortedItems.filter((i) => selected.has(i.id));
      const existingIds = await fetchExistingChecklistTodoIds();
      const duplicates = picked.filter((it) => existingIds.has(it.id));
      const newOnes = picked.filter((it) => !existingIds.has(it.id));

      // Case 1 — every selected item is already a to-do.
      if (duplicates.length > 0 && newOnes.length === 0) {
        setBusy(false);
        setConfirm({
          title: 'Already in your To-Do list',
          description: (
            <>
              <p className="mb-2">
                The following item{duplicates.length === 1 ? ' is' : 's are'} already in your To-Do list:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                {duplicates.slice(0, 8).map((d) => (
                  <li key={d.id} className="text-gray-800"><strong>{d.item}</strong></li>
                ))}
                {duplicates.length > 8 && (
                  <li className="text-gray-500">…and {duplicates.length - 8} more</li>
                )}
              </ul>
            </>
          ),
          confirmLabel: 'OK',
          cancelLabel: 'Close',
          tone: 'default',
          onConfirm: async () => { /* informational only */ },
        });
        return;
      }

      // Case 2 — mix of new + duplicate. Ask before silently skipping.
      if (duplicates.length > 0 && newOnes.length > 0) {
        setBusy(false);
        setConfirm({
          title: 'Some items are already in your To-Do list',
          description: (
            <>
              <p className="mb-2">
                {duplicates.length} of the {picked.length} selected item{picked.length === 1 ? '' : 's'} {duplicates.length === 1 ? 'is' : 'are'} already in your To-Do list and will be skipped:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm mb-2">
                {duplicates.slice(0, 6).map((d) => (
                  <li key={d.id} className="text-gray-800"><strong>{d.item}</strong></li>
                ))}
                {duplicates.length > 6 && (
                  <li className="text-gray-500">…and {duplicates.length - 6} more</li>
                )}
              </ul>
              <p className="text-gray-700">
                Add the remaining <strong>{newOnes.length}</strong> as new to-do{newOnes.length === 1 ? '' : 's'}?
              </p>
            </>
          ),
          confirmLabel: `Add ${newOnes.length}`,
          tone: 'default',
          onConfirm: async () => {
            setBusy(true);
            try {
              await createTodos(newOnes);
              toast.success(`${newOnes.length} to-do${newOnes.length === 1 ? '' : 's'} added to your planner`);
              setSelected(new Set());
            } catch (err: any) {
              toast.error(err?.response?.data?.detail || 'Failed to add to-dos');
              throw err;
            } finally {
              setBusy(false);
            }
          },
        });
        return;
      }

      // Case 3 — no duplicates, plain add.
      await createTodos(newOnes);
      toast.success(`${newOnes.length} to-do${newOnes.length === 1 ? '' : 's'} added to your planner`);
      setSelected(new Set());
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to add to-dos');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gt-card p-5 mb-6" style={{ position: 'relative' }}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div style={{
            width: 36, height: 36, borderRadius: 11,
            background: 'var(--gt-grad-aurora)',
            color: '#ffffff',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'var(--gt-glow-soft), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}>
            <ClipboardList className="w-4 h-4" />
          </div>
          <div>
            <h3 className="gt-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--gt-text-primary)', lineHeight: 1.2 }}>Checklist</h3>
            {items.length > 0 && (
              <span className="gt-page-sub gt-numerals" style={{ fontSize: 11.5, marginTop: 2, display: 'block' }}>
                <span className="gt-display" style={{ color: 'var(--gt-accent-strong)' }}>{completedCount}</span>
                {' '}of <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{items.length}</span> completed
              </span>
            )}
          </div>
        </div>

        {/* Selection toolbar */}
        {sortedItems.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={toggleSelectAll} className="gt-btn is-ghost is-sm" disabled={busy}>
              <span style={{
                width: 16, height: 16, borderRadius: 4,
                border: `2px solid ${allSelected ? 'var(--gt-primary)' : 'var(--gt-border-strong)'}`,
                background: allSelected ? 'var(--gt-primary)' : 'transparent',
                color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {allSelected && <Check className="w-2.5 h-2.5" strokeWidth={4} />}
              </span>
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>

            {hasSelection && (
              <>
                <span className="gt-pill is-personal gt-numerals">{selected.size} selected</span>
                <button
                  onClick={() => {
                    if (lockToggle) {
                      toast.warning(lockMessage || 'Start the task first to mark items done.');
                      return;
                    }
                    markSelectedComplete();
                  }}
                  className="gt-btn is-accent is-sm"
                  disabled={busy || lockToggle}
                  title={lockToggle ? (lockMessage || 'Start the task first to mark items done.') : undefined}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Mark complete
                </button>
                <button onClick={addSelectedAsTodos} className="gt-btn is-primary is-sm" disabled={busy}>
                  <ListPlus className="w-3.5 h-3.5" />
                  Add as To-Do
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add new item */}
      <form onSubmit={handleAdd} className="flex items-start gap-2 mb-4">
        <AutoGrowTextarea
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => {
            // Enter adds the item (Shift+Enter inserts a newline for multi-line text).
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder="Add a new item…"
          className="gt-input flex-1"
        />
        <button
          type="submit"
          disabled={adding || !newItem.trim()}
          className="gt-btn is-primary"
        >
          <Plus className="w-4 h-4" />
          {adding ? 'Adding…' : 'Add Item'}
        </button>
      </form>

      {/* Items */}
      <div className="space-y-2 max-h-96 overflow-y-auto gt-scroll" style={{ paddingRight: 4 }}>
        {sortedItems.length === 0 ? (
          <div className="gt-empty">
            <div className="gt-empty-leaf"><Sparkles className="w-6 h-6" /></div>
            <div className="gt-empty-title">No checklist items</div>
            <div className="gt-page-sub">Add items to <em className="gt-serif-italic">track subtasks</em>.</div>
          </div>
        ) : (
          sortedItems.map((item, idx) => {
            const isSelected = selected.has(item.id);
            const isEditing = editingId === item.id;
            const serial = sortedItems.length - idx;
            return (
              <div
                key={item.id}
                className="group flex items-center gap-3"
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  background: isSelected ? 'var(--gt-primary-container)' : 'var(--gt-surface-1)',
                  border: `1px solid ${isSelected ? 'rgba(16, 185, 129, 0.45)' : 'var(--gt-divider)'}`,
                  transition: 'all 160ms cubic-bezier(0.33,1,0.68,1)',
                }}
              >
                {/* Serial number — first, to the left of both checkboxes */}
                <span
                  className="gt-numerals"
                  style={{
                    minWidth: 22,
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--gt-text-tertiary)',
                    textAlign: 'right',
                    flexShrink: 0,
                  }}
                >
                  {serial}.
                </span>

                {/* Selection checkbox */}
                <button
                  type="button"
                  onClick={() => toggleSelect(item.id)}
                  aria-label="Select"
                  style={{
                    width: 18, height: 18, borderRadius: 5,
                    border: `2px solid ${isSelected ? 'var(--gt-primary)' : 'var(--gt-border-strong)'}`,
                    background: isSelected ? 'var(--gt-primary)' : 'transparent',
                    color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    cursor: 'pointer',
                    transition: 'all 160ms',
                  }}
                >
                  {isSelected && <Check className="w-2.5 h-2.5" strokeWidth={4} />}
                </button>

                {/* Done toggle (circle) — disabled until the task is started */}
                <button
                  type="button"
                  onClick={() => {
                    if (lockToggle) {
                      toast.warning(lockMessage || 'Start the task first to mark items done.');
                      return;
                    }
                    handleToggle(item.id, item.is_done);
                  }}
                  disabled={lockToggle}
                  aria-label="Toggle done"
                  title={lockToggle ? (lockMessage || 'Start the task first to mark items done.') : undefined}
                  style={{
                    width: 22, height: 22, borderRadius: 999,
                    border: `2px solid ${item.is_done ? 'var(--gt-accent-strong)' : 'var(--gt-border-strong)'}`,
                    background: item.is_done ? 'var(--gt-accent-strong)' : 'transparent',
                    color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    cursor: lockToggle ? 'not-allowed' : 'pointer',
                    opacity: lockToggle ? 0.5 : 1,
                  }}
                >
                  {item.is_done && <Check className="w-3 h-3" strokeWidth={3} />}
                </button>

                {/* Item text / edit input */}
                {isEditing ? (
                  <AutoGrowTextarea
                    autoFocus
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter saves (Shift+Enter inserts a newline for multi-line text).
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    onBlur={saveEdit}
                    className="gt-input flex-1"
                    style={{ padding: '6px 10px', fontSize: 13 }}
                  />
                ) : (
                  <span className="flex-1 gt-display" style={{
                    fontSize: 13.5, fontWeight: 500,
                    color: item.is_done ? 'var(--gt-text-disabled)' : 'var(--gt-text-primary)',
                    textDecoration: item.is_done ? 'line-through' : 'none',
                  }}>
                    {item.item}
                  </span>
                )}

                {/* Inline action icons */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {isEditing ? (
                    <>
                      <button onMouseDown={(e) => { e.preventDefault(); saveEdit(); }} className="gt-icon-btn is-sm" aria-label="Save">
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button onMouseDown={(e) => { e.preventDefault(); setEditingId(null); }} className="gt-icon-btn is-sm" aria-label="Cancel">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(item)} className="gt-icon-btn is-sm" aria-label="Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="gt-icon-btn is-sm" style={{ color: 'var(--gt-dnd)' }} aria-label="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer celebration */}
      {items.length > 0 && completedCount === items.length && (
        <div className="mt-4 p-3" style={{
          background: 'linear-gradient(135deg, rgba(167, 243, 208, 0.55) 0%, rgba(209, 250, 229, 0.45) 100%)',
          border: '1px solid rgba(52, 211, 153, 0.45)',
          borderRadius: 12,
          textAlign: 'center',
        }}>
          <span className="gt-display" style={{ fontSize: 13, fontWeight: 700, color: 'var(--gt-primary-deep)' }}>
            🎉 All <em className="gt-serif-italic">{items.length}</em> items completed!
          </span>
        </div>
      )}

      {/* Popup for already-in-todo notice / partial-add confirmation */}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
